import type {AttributionChannel} from "attribution/adAttribution";

/**
 * Whether THIS document arrived from the recipient funnel, and whether its first send still owes a
 * report (ui#1973). React-free by mandate (epic #26).
 *
 * ── What `recipient.send.first` claims, exactly ──────────────────────────────────────────────────
 * It claims ONE thing, and it is narrower than "a recipient converted": a send began in a document
 * whose own acquisition bucket was one of {@link RECIPIENT_FUNNEL_CHANNELS} — `recipient-page`, either
 * of the transfer-games arms that split the download route since ui#2963, or, since ui#2253 split the
 * file-request landing out of it, `request-page`. Read the panel as that sentence, never as a
 * conversion rate.
 *
 * It covers the journey the funnel exists to measure — a recipient follows the gold chip into a new
 * tab, signs up there, and composes a transfer without leaving it — because the claim link lands on
 * this site's own origin and `adAttribution` classifies that landing as `recipient-page`.
 *
 * <b>It also counts sends this funnel did not win, and the over-count is not marginal.</b> Each of those
 * buckets is earned by ANY landing on its route, not only by a chip claim, and the composer is
 * reachable from a download page WITHOUT a new document: the app-wide brand mark pushes
 * `/` client-side on a full-site host (`nav/siteLinkOrigin.ts`). So all of these are counted:
 *
 *  - a SENDER opening their own transfer's download link and then sending another transfer;
 *  - an established account holder who receives a file and later sends one;
 *  - a recipient who never touched the offer chip at all.
 *
 * `classify` names the first of those and accepts it, because the SIGNUP counter it was written for is
 * fenced by the api's account-establishment seam — an established sender establishes nothing, so
 * nothing is counted. That argument does not carry over here: this counter has no fence, and fires
 * from the client's arrival bucket alone.
 *
 * Narrowing it USED to need something the landing does not give the sending document — the chip claim
 * specifically, or the absence of an established account. The transfer-games split changes that, and the
 * change is not acted on here (ui#2963). Once the ui half ships, a download-route landing answers one of
 * the two arms and plain `recipient-page` is left naming the chip claim alone (rank 4 in `classify`), so
 * "the chip journey only" becomes readable off the channel with no extra state. That makes it a real
 * choice rather than an impossibility: keep the wider "any page somebody was sent to" population, which
 * is what this counter has always meant and what {@link RECIPIENT_FUNNEL_CHANNELS} keeps, or narrow to
 * chip claims and accept a different number under the same name. This counter deliberately keeps the
 * wider one until the owner rules, because a silent narrowing is the failure mode the constant below
 * exists to prevent. Do not narrow it as a side effect of another ticket.
 *
 * It also UNDER-counts in one direction: a document is the unit, so a recipient who reaches the
 * composer through a full page navigation starts a fresh document with no honest bucket — our own
 * `<meta name="referrer" content="origin">` strips the path, so nothing there can tell "came from a
 * download page" from "reloaded the home page". Such a send is not counted.
 *
 * ── Why memory rather than sessionStorage, which would count more ────────────────────────────────
 * A per-tab `sessionStorage` flag would survive that full navigation and widen the count. It is not used
 * because `adAttribution` already refuses exactly this write: it keeps the landing in MEMORY ONLY
 * "because writing it would be non-essential storage before consent", and a funnel flag is non-essential
 * by definition — nothing the recipient asked for stops working without it. That measurement path now
 * writes NOTHING before a consent decision on any leg, including a provider sign-in redirect (ui#2331
 * item A removed the one entry that did), so there is no precedent here to follow. Counting fewer sends
 * is the cheaper mistake.
 */

/**
 * The buckets that mean "this document arrived on a page somebody was sent to".
 *
 * BOTH recipient surfaces, which is what this counter has always meant: ui#2253 split the file-request
 * landing out of `recipient-page` into `request-page` for the CHANNEL, and listing only the first word
 * here would have silently narrowed a shipped funnel — the same population, counted less, with no test
 * or panel saying when it changed. The owner's ruling splits the acquisition counter, not this one.
 *
 * ui#2963 is that same rule applied a second time, and it is the reason the arms are here BEFORE the ui
 * can answer either word. The api's transfer-games split replaces the download-route landing with
 * `recipient-page-game` or `recipient-page-control` per A/B arm, leaving plain `recipient-page` for the
 * referral chip alone. So on the day the ui half ships, a list of two words would drop this funnel's
 * ENTIRE download-route population — the failure the paragraph above describes, at full size, and again
 * with nothing saying when it changed. The arm a page load was drawn into is not a different acquisition.
 */
const RECIPIENT_FUNNEL_CHANNELS: ReadonlyArray<AttributionChannel> = [
	"recipient-page", "recipient-page-game", "recipient-page-control", "request-page",
];

/**
 * Whether a document with this acquisition bucket arrived from the recipient funnel.
 *
 * A function over the CHANNEL rather than over the path, so it inherits every rule `classify` already
 * owns — landing on a download or file-request page, and following the offer chip onto our own origin —
 * instead of restating any of them. A second path test here would be free to disagree with the one the
 * signup counter uses, and the two numbers are meant to be read against each other.
 */
export function arrivedFromRecipientPage(channel: AttributionChannel | undefined): boolean {
	return channel !== undefined && RECIPIENT_FUNNEL_CHANNELS.includes(channel);
}

/**
 * The document's arrival flag: set once when the document is recognised, spent by the first send.
 *
 * Spend-on-read is what makes the counter FIRST send rather than every send. A recipient who sends three
 * transfers in one sitting converted once, and counting three would make the conversion rate a function
 * of how much somebody happened to send.
 */
export interface RecipientArrival {
	/**
	 * Record that this document arrived from the recipient funnel.
	 *
	 * Idempotent BEFORE the flag is spent — marking twice still owes one send. Marking again AFTER a
	 * consume re-arms it, which would owe a second; nothing does that today, because the only caller
	 * marks once at wire time, long before any send. Do not add a second caller without deciding which
	 * of the two this counter means.
	 */
	mark(): void;
	/** Whether a send starting now is the first one this document owes, spending the flag if so. */
	consume(): boolean;
}

/**
 * A factory with a singleton beside it, so a test drives its own instance rather than reaching for a
 * reset the production code would otherwise have to carry for nobody's benefit.
 */
export function createRecipientArrival(): RecipientArrival {
	let arrived = false;
	return {
		mark() {
			arrived = true;
		},
		consume() {
			const owed = arrived;
			arrived = false;
			return owed;
		},
	};
}

/** This document's arrival flag. One per document, which is exactly the lifetime the claim above needs. */
export const recipientArrival: RecipientArrival = createRecipientArrival();
